/**
 * AutoCAD block-name predicates and anonymous-name generators.
 *
 * AutoCAD uses a small number of `*`-prefixed patterns for blocks that
 * it auto-generates (layouts, anonymous dimension blocks, unnamed
 * groups, xref references). User-authored blocks never start with `*`.
 *
 * Anonymous block patterns:
 *   *Model_Space           — canonical model-space block
 *   *Paper_Space           — first paper-space (layout) block
 *   *Paper_Space<N>        — additional paper-space blocks (Layout2, …)
 *   *D<N>                  — anonymous dimension block (one per DIMENSION)
 *   *U<N>                  — anonymous hatch / unnamed group block
 *   *X<N>                  — anonymous xref / external-reference block
 *   *E<N>                  — anonymous construction block (less common)
 *   *T<N>                  — anonymous ACAD_TABLE graphics block
 *
 * Public surface:
 *   - isAnonymousBlockName(name)  — the PURGE-reservation patterns above
 *   - isRenameHiddenBlockName(n)  — the RENAME guard (every `*` block)
 *   - isLayoutBlockName(name)     — Model_Space / Paper_Space / Paper_SpaceN
 *   - isDimensionBlockName(name)  — *D<N>
 *   - isHatchBlockName(name)      — *U<N>
 *   - isXrefBlockName(name)       — *X<N>
 *   - isTableGraphicsBlockName(n) — *T<N>
 *   - nextAnonymousBlockName(prefix, existing) — allocator
 *
 * `isAnonymousBlockName` and `isRenameHiddenBlockName` are DELIBERATELY two
 * predicates over two different questions — see their docs. Collapsing them
 * into one re-opens whichever bug the other one closes.
 *
 * CLAUDE.md #1: no shortcuts, mirror AutoCAD's anonymous-name scheme
 * exactly. Predicates are case-insensitive — AutoCAD treats `*d1` and
 * `*D1` as equivalent (block names are folded to uppercase on disk).
 */

/**
 * Any AutoCAD anonymous block (layout, dimension, hatch, xref, …).
 *
 * This predicate answers ONE question: **is this name RESERVED from PURGE**
 * (`purgeBlocksMutation`'s `skipReserved`). It deliberately does NOT match
 * `*T<N>`, the ACAD_TABLE graphics block: reserving it would make an
 * ORPHANED graphics block — its table erased — permanently unpurgeable,
 * trading a deletion bug for a leak, and neither AutoCAD nor ezdxf does that
 * (`doc.purge()` removes unreferenced anonymous blocks; only the layout
 * blocks are structural). A LIVE table's block is protected by the
 * DEPENDENCY instead (`dependencyGraph.extractRefs` §Table), which is exact
 * in both directions. That decision is pinned by
 * `tableGraphicsBlockDependency.test.ts` and must not be reversed here.
 *
 * For the RENAME question — a different question with a different answer —
 * use {@link isRenameHiddenBlockName}.
 */
export function isAnonymousBlockName(name: string): boolean {
  return /^\*(?:Model_Space|Paper_Space\d*|D\d+|U\d+|X\d+|E\d+)$/iu.test(name);
}

/**
 * A block AutoCAD's RENAME never offers and never renames: every
 * system-generated `*`-prefixed block.
 *
 * ## Why this is separate from {@link isAnonymousBlockName}
 *
 * PURGE asks "may this be DELETED when nothing references it?" — for an
 * orphaned `*T<N>` the answer is yes. RENAME asks "may this be RENAMED while
 * something still references it?" — for `*T<N>` the answer is no, and the two
 * answers are not the same answer. Renaming a live table's graphics block
 * moved the definition while every `Table.blockName` kept the OLD name (the
 * rename cascade rewrites INSERT `blockName` and the MINSERT-shaped
 * `sourceBlockName`, nothing else), so the table instantly rendered empty
 * (PR #785 @codex round 2, F3).
 *
 * ## Why the whole `*` prefix, not `isAnonymousBlockName` plus `*T`
 *
 * Autodesk: block names beginning with `*` are the ones AutoCAD generates for
 * itself, and RENAME does not list them. A user cannot author such a name
 * either — `*` is in the command engine's `FORBIDDEN_BLOCK_NAME_CHARS` — so
 * this rule can never hide a name a user could have created, and it needs no
 * per-family enumeration: the next `*`-family AutoCAD invents is covered
 * without anyone remembering to add it here. Extending the rename CASCADE
 * per referencing entity type instead would be an allow-list that goes stale
 * silently — the exact shape that produced this defect.
 */
export function isRenameHiddenBlockName(name: string): boolean {
  return /^\*/u.test(name.trim());
}

/** Model-space or paper-space layout block. */
export function isLayoutBlockName(name: string): boolean {
  return /^\*(?:Model_Space|Paper_Space\d*)$/iu.test(name);
}

/**
 * Paper-space layout block ONLY (`*Paper_Space` / `*Paper_Space<N>`) —
 * excludes `*Model_Space`. Used by LAYOUT DELETE to discard the BLOCK
 * definition + BLOCK_RECORD owned by the removed layout (AutoCAD /
 * ezdxf `Layouts.delete()` semantics) without ever matching the
 * model-space block.
 */
export function isPaperSpaceBlockName(name: string): boolean {
  return /^\*Paper_Space\d*$/iu.test(name);
}

/** Anonymous dimension block — one per DIMENSION entity in the drawing. */
export function isDimensionBlockName(name: string): boolean {
  return /^\*D\d+$/iu.test(name);
}

/** Anonymous hatch / unnamed group block. */
export function isHatchBlockName(name: string): boolean {
  return /^\*U\d+$/iu.test(name);
}

/**
 * Anonymous ACAD_TABLE graphics block (`*T<N>`) — the block AutoCAD
 * regenerates a table's rendered cells and grid lines into, referenced by the
 * table's DXF group 2. NOT part of {@link isAnonymousBlockName} (that would
 * leak an orphan past PURGE); it is covered by
 * {@link isRenameHiddenBlockName} for the rename question.
 */
export function isTableGraphicsBlockName(name: string): boolean {
  return /^\*T\d+$/iu.test(name);
}

/** Anonymous xref / external-reference block. */
export function isXrefBlockName(name: string): boolean {
  return /^\*X\d+$/iu.test(name);
}

/** Anonymous construction block (dynamic-block authoring). */
export function isConstructionBlockName(name: string): boolean {
  return /^\*E\d+$/iu.test(name);
}

/**
 * Allocate the next free anonymous block name under a given `*D` /
 * `*U` / `*X` / `*E` prefix by scanning the `existing` map. The returned
 * name is guaranteed not to collide with any key in the map (case-
 * insensitive). Callers then seed that name into their existing map
 * before the next call if they need multiple unique allocations.
 */
export function nextAnonymousBlockName(
  prefix: "*D" | "*U" | "*X" | "*E",
  existing: Record<string, unknown>,
): string {
  // Collect every numeric suffix already consumed under this prefix.
  const used = new Set<number>();
  const re = new RegExp(`^\\${prefix}(\\d+)$`, "iu");
  for (const key of Object.keys(existing)) {
    const match = key.match(re);
    if (match && match[1]) {
      const n = Number.parseInt(match[1], 10);
      if (Number.isFinite(n) && n > 0) used.add(n);
    }
  }
  // Lowest unused positive integer.
  let counter = 1;
  while (used.has(counter)) counter += 1;
  return `${prefix}${counter}`;
}
